// src/api/finance-api.js
import axios from "axios";
import ParamsUtil from "utils/params-util";

// make sure cookies/session go with every request
axios.defaults.withCredentials = true;
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

const API_BASE = "/api/v2/admins/finance";   // <-- leading slash (important on nested routes)
const POUT     = `${API_BASE}/payouts`;
const TX       = `${API_BASE}/payout_transactions`;
const DEAL     = `${API_BASE}/deals`;

const FinanceApi = {
  uploads: {
    presign: async ({ filename, content_type }) =>
      axios.post(`${API_BASE}/uploads/presign`, { filename, content_type }),
  },
  // ---------- Curator (queue  submit) ----------
  curator: {
    queue: async (rawParams = {}) => {
      const params = ParamsUtil.removeEmptyAttributes({ page: 1, per_page: 25, q: "", sku: "", ...rawParams });
      return axios.get(`${POUT}/curator_queue`, { params });
    },

    submit: async ({ id, buying_price, assist_by_ids, ktp_url, deal_proof_url, bank_name, bank_account_number, bank_account_holder_name }) => {
    try {
      // 1) call the route you actually defined:
      return await axios.post(`${POUT}/${id}/curator_submit`, {
        buying_price, assist_by_ids, ktp_url, deal_proof_url,
        bank_name, bank_account_number, bank_account_holder_name,
      });
    } catch (e) {
      // 2) compatibility fallback (optional)
      try {
        return await axios.post(`${POUT}/curator/${id}/submit`, {
          buying_price, assist_by_ids, ktp_url, deal_proof_url,
          bank_name, bank_account_number, bank_account_holder_name,
        });
      } catch (ee) {
        // 3) final fallback to legacy v3
        if (ee?.response?.status !== 404) throw ee;
        return axios.post(`/api/v3/revenues/consignor/curator/${id}/submit`, {
          buying_price, assist_by_ids, ktp_url, deal_proof_url,
          bank_name, bank_account_number, bank_account_holder_name,
        });
        }
      }
    },
  },

  payouts: {
    get: async (rawParams = {}) => {
      const params = ParamsUtil.removeEmptyAttributes({ page: 1, per_page: 25, ...rawParams });
      return axios.get(POUT, { params });
    },

    getPerItem: async (rawParams = {}) =>
      FinanceApi.payouts.get({ per_item: 1, ...rawParams }),

    show: async ({ id, hj_only } = {}) =>
      axios.get(`${POUT}/${id}`, { params: ParamsUtil.removeEmptyAttributes({ hj_only }) }),

    submit: async ({ id }) =>
      axios.post(`${POUT}/${id}/submit`),
    
    financeQueue: async (rawParams = {}) => {
      const params = ParamsUtil.removeEmptyAttributes({ page: 1, per_page: 25, q: "", sku: "", ...rawParams });
      return axios.get(`${POUT}/finance_queue`, { params });
    },
    
    confirmPayment: async ({ id, amount, payment_kind, proof_key }) =>
      axios.post(`${POUT}/${id}/confirm_payment`, { amount, payment_kind, proof_key }),

    reject: async ({ id, reason }) =>
      axios.post(`${POUT}/${id}/reject`, { reason }),

    // 🔧 NEW: update (status/scheduled_on/note/force …)
    update: async ({ id, ...payload }) =>
      axios.patch(`${POUT}/${id}`, ParamsUtil.removeEmptyAttributes(payload)),

    presignUpload: async ({ id, filename, content_type }) =>
      axios.post(`${POUT}/${id}/uploads/presign`, { filename, content_type }),
  },

  payoutTransactions: {
    uploadProof: async ({ id, formData }) =>
      axios.post(`${TX}/${id}/upload_proof`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    removeProof: async ({ id }) =>
      axios.delete(`${TX}/${id}/remove_proof`),
  },

  dealProofs: {
    index:   async ({ deal_id })         => axios.get(`${DEAL}/${deal_id}/deal_proofs`),
    create:  async ({ deal_id, file_url }) => axios.post(`${DEAL}/${deal_id}/deal_proofs`, { file_url }),
    destroy: async ({ deal_id, id })     => axios.delete(`${DEAL}/${deal_id}/deal_proofs/${id}`),
  },

  // shims
  get:  async (endpoint, config = {})            => axios.get(endpoint,  config),
  post: async (endpoint, body = {}, config = {}) => axios.post(endpoint, body, config),
};

export default FinanceApi;
